import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { api } from '@/api/client'
import { ArrowLeft, Plus, Trash2, Loader2, Image as ImageIcon, ChevronUp, ChevronDown } from 'lucide-react'

interface ImageSource {
  primary: string
  r2Key?: string | null
  width?: number | null
  height?: number | null
  size?: number | null
  format?: string | null
  updatedAt?: string | null
  backupStatus?: string
  primaryStatus?: string
  primaryFailedAt?: string | null
  primaryFailCount?: number
  lastError?: string | null
  lastCheckedAt?: string | null
}

interface Platform {
  id: string
  name: string
  url: string
  status: string
  priority: number
  description?: string
  icon?: string
}

interface GameForm {
  id: string
  name: string
  title: string
  slug: string
  status: 'active' | 'hidden'
  category: string
  tags: string[]
  platforms: Platform[]
  images: {
    cover: ImageSource
    icon: ImageSource
    screenshots: ImageSource[]
    banner?: ImageSource
  }
  content: {
    intro1: string
    intro2: string
  }
}

const categories = [
  { id: 'rpg', name: 'RPG' },
  { id: 'action', name: '动作' },
  { id: 'strategy', name: '策略' },
  { id: 'casual', name: '休闲' },
  { id: 'social', name: '社交' },
  { id: 'other', name: '其他' }
]

export default function GameEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<GameForm>({
    id: '',
    name: '',
    title: '',
    slug: '',
    status: 'active',
    category: 'rpg',
    tags: [],
    platforms: [],
    images: {
      cover: { primary: '', updatedAt: new Date().toISOString(), backupStatus: 'pending' },
      icon: { primary: '', updatedAt: new Date().toISOString(), backupStatus: 'pending' },
      screenshots: [],
      banner: { primary: '', updatedAt: new Date().toISOString(), backupStatus: 'pending' }
    },
    content: {
      intro1: '',
      intro2: ''
    }
  })

  useEffect(() => {
    if (!isNew && id) {
      fetchGame(id)
    }
  }, [id])

  const fetchGame = async (gameId: string) => {
    try {
      const res = await api.get(`/api/games/${gameId}`)
      const game = res.data.data?.game || res.data.data
      if (!game) {
        console.error('Game data not found in response:', res.data)
        alert('获取游戏数据失败')
        return
      }
      setForm({
        id: game.id || '',
        name: game.name || '',
        title: game.title || '',
        slug: game.slug || '',
        status: game.status || 'active',
        category: game.category || 'rpg',
        tags: game.tags || [],
        platforms: game.platforms || [],
        images: {
          cover: game.images?.cover || { primary: '', updatedAt: new Date().toISOString(), backupStatus: 'pending' },
          icon: game.images?.icon || { primary: '', updatedAt: new Date().toISOString(), backupStatus: 'pending' },
          screenshots: game.images?.screenshots || [],
          banner: game.images?.banner || { primary: '', updatedAt: new Date().toISOString(), backupStatus: 'pending' }
        },
        content: {
          intro1: game.content?.intro1 || '',
          intro2: game.content?.intro2 || ''
        }
      })
    } catch (error) {
      console.error('Failed to fetch game:', error)
      alert('获取游戏数据失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (isNew) {
        await api.post('/admin/games', form)
      } else {
        await api.put(`/admin/games/${id}`, form)
      }
      navigate('/games')
    } catch (error) {
      console.error('Failed to save game:', error)
      alert('保存失败')
    } finally {
      setSaving(false)
    }
  }

  const addPlatform = () => {
    setForm({
      ...form,
      platforms: [...form.platforms, { id: '', name: '', url: '', status: 'active', priority: 1 }]
    })
  }

  const updatePlatform = (index: number, field: keyof Platform, value: string | number) => {
    const newPlatforms = [...form.platforms]
    newPlatforms[index] = { ...newPlatforms[index], [field]: value }
    setForm({ ...form, platforms: newPlatforms })
  }

  const removePlatform = (index: number) => {
    setForm({
      ...form,
      platforms: form.platforms.filter((_, i) => i !== index)
    })
  }

  const addScreenshot = () => {
    setForm({
      ...form,
      images: {
        ...form.images,
        screenshots: [...form.images.screenshots, { primary: '', updatedAt: new Date().toISOString(), backupStatus: 'pending' }]
      }
    })
  }

  const updateScreenshot = (index: number, value: string) => {
    const newScreenshots = [...form.images.screenshots]
    newScreenshots[index] = { ...newScreenshots[index], primary: value }
    setForm({
      ...form,
      images: { ...form.images, screenshots: newScreenshots }
    })
  }

  const removeScreenshot = (index: number) => {
    setForm({
      ...form,
      images: {
        ...form.images,
        screenshots: form.images.screenshots.filter((_, i) => i !== index)
      }
    })
  }

  const moveScreenshot = (index: number, direction: 'up' | 'down') => {
    const newScreenshots = [...form.images.screenshots]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= newScreenshots.length) return
    ;[newScreenshots[index], newScreenshots[targetIndex]] = [newScreenshots[targetIndex], newScreenshots[index]]
    setForm({
      ...form,
      images: { ...form.images, screenshots: newScreenshots }
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 头部 */}
      <div className="flex items-center gap-4">
        <Link
          to="/games"
          className="p-2 rounded-lg hover:bg-secondary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{isNew ? '新建游戏' : '编辑游戏'}</h1>
        </div>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          保存
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 基本信息 */}
        <section className="rounded-lg border border-border bg-card p-6 space-y-4">
          <h2 className="font-semibold">基本信息</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">ID</label>
              <input
                type="text"
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value })}
                disabled={!isNew}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="game-id"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">名称</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="游戏名称"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">完整标题</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="游戏完整标题"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">分类</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">状态</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as 'active' | 'hidden' })}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="active">上线</option>
                <option value="hidden">隐藏</option>
              </select>
            </div>
          </div>
        </section>

        {/* 平台链接 */}
        <section className="rounded-lg border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">平台链接</h2>
            <button
              type="button"
              onClick={addPlatform}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-secondary text-secondary-foreground hover:opacity-90 transition-opacity text-sm"
            >
              <Plus className="w-4 h-4" />
              添加平台
            </button>
          </div>

          <div className="space-y-3">
            {form.platforms.map((platform, index) => (
              <div key={index} className="flex gap-2 items-start">
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    value={platform.id}
                    onChange={(e) => {
                      const val = e.target.value
                      const newPlatforms = [...form.platforms]
                      newPlatforms[index] = { ...newPlatforms[index], id: val.toLowerCase().replace(/[^a-z0-9_-]/g, '') }
                      setForm({ ...form, platforms: newPlatforms })
                    }}
                    placeholder="平台ID (英文)"
                    className="w-28 px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                  />
                  <span className="text-xs text-muted-foreground">唯一标识</span>
                </div>
                <input
                  type="text"
                  value={platform.name}
                  onChange={(e) => {
                    const val = e.target.value
                    const newPlatforms = [...form.platforms]
                    newPlatforms[index] = { ...newPlatforms[index], name: val }
                    setForm({ ...form, platforms: newPlatforms })
                  }}
                  placeholder="平台名称"
                  className="w-36 px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <input
                  type="url"
                  value={platform.url}
                  onChange={(e) => updatePlatform(index, 'url', e.target.value)}
                  placeholder="游戏链接"
                  className="flex-1 px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  type="button"
                  onClick={() => removePlatform(index)}
                  className="p-2 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {form.platforms.length === 0 && (
              <p className="text-muted-foreground text-sm">点击上方按钮添加平台链接</p>
            )}
          </div>
        </section>

        {/* 图片 */}
        <section className="rounded-lg border border-border bg-card p-6 space-y-4">
          <h2 className="font-semibold">图片</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">封面图 URL</label>
              <input
                type="url"
                value={form.images.cover.primary}
                onChange={(e) => setForm({
                  ...form,
                  images: { ...form.images, cover: { ...form.images.cover, primary: e.target.value } }
                })}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="https://..."
              />
              {form.images.cover.primary && (
                <img src={form.images.cover.primary} alt="封面预览" className="mt-2 h-24 w-auto rounded-lg object-cover" />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">图标 URL</label>
              <input
                type="url"
                value={form.images.icon.primary}
                onChange={(e) => setForm({
                  ...form,
                  images: { ...form.images, icon: { ...form.images.icon, primary: e.target.value } }
                })}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="https://..."
              />
              {form.images.icon.primary && (
                <img src={form.images.icon.primary} alt="图标预览" className="mt-2 h-16 w-16 rounded-lg object-cover" />
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">横幅 URL</label>
            <input
              type="url"
              value={form.images.banner?.primary || ''}
              onChange={(e) => setForm({
                ...form,
                images: { ...form.images, banner: { ...(form.images.banner || {}), primary: e.target.value } }
              })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="https://... (可选)"
            />
            {form.images.banner?.primary && (
              <img src={form.images.banner.primary} alt="横幅预览" className="mt-2 h-20 w-auto rounded-lg object-cover" />
            )}
          </div>
        </section>

        {/* 截图 */}
        <section className="rounded-lg border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">游戏截图</h2>
            <button
              type="button"
              onClick={addScreenshot}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-secondary text-secondary-foreground hover:opacity-90 transition-opacity text-sm"
            >
              <Plus className="w-4 h-4" />
              添加截图
            </button>
          </div>

          <div className="space-y-3">
            {form.images.screenshots.map((screenshot, index) => (
              <div key={index} className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground w-8 shrink-0">#{index + 1}</span>
                  <input
                    type="url"
                    value={screenshot.primary}
                    onChange={(e) => updateScreenshot(index, e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    placeholder="截图 URL https://..."
                  />
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveScreenshot(index, 'up')}
                      disabled={index === 0}
                      className="p-1.5 rounded hover:bg-secondary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="上移"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveScreenshot(index, 'down')}
                      disabled={index === form.images.screenshots.length - 1}
                      className="p-1.5 rounded hover:bg-secondary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="下移"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeScreenshot(index)}
                      className="p-1.5 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors"
                      title="删除"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {screenshot.primary && (
                  <div className="ml-10">
                    <img src={screenshot.primary} alt={`截图 ${index + 1}`} className="h-32 w-auto rounded-lg object-cover border border-border" />
                    {screenshot.backupStatus && (
                      <span className={`text-xs mt-1 inline-block px-1.5 py-0.5 rounded ${
                        screenshot.backupStatus === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        screenshot.backupStatus === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {screenshot.backupStatus === 'active' ? '已备份' : screenshot.backupStatus === 'failed' ? '备份失败' : '待备份'}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
            {form.images.screenshots.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <ImageIcon className="w-10 h-10 mb-2 opacity-50" />
                <p className="text-sm">暂无截图，点击上方按钮添加</p>
              </div>
            )}
          </div>
        </section>

        {/* 简介 */}
        <section className="rounded-lg border border-border bg-card p-6 space-y-4">
          <h2 className="font-semibold">简介</h2>
          
          <div>
            <label className="block text-sm font-medium mb-1">短简介</label>
            <textarea
              value={form.content.intro1}
              onChange={(e) => setForm({
                ...form,
                content: { ...form.content, intro1: e.target.value }
              })}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="简短的游戏介绍..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">详细描述</label>
            <textarea
              value={form.content.intro2}
              onChange={(e) => setForm({
                ...form,
                content: { ...form.content, intro2: e.target.value }
              })}
              rows={6}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="详细的游戏描述..."
            />
          </div>
        </section>
      </form>
    </div>
  )
}
